/* Lunéa editorial PDP sync — keeps Dawn's variant fetch, updates only the elements that change. */
if (!customElements.get('lunea-pdp-sync')) {
  customElements.define(
    'lunea-pdp-sync',
    class LuneaPdpSync extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.root = this.closest('product-info');
        if (!this.root) return;

        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.sectionId = String(this.dataset.sectionId || this.root.dataset.section || '');
        this.quantity = this.root.querySelector('.quantity__input');
        this.desiredQuantity = this.quantity?.value || '1';
        this.serverBlocked = !!this.root.querySelector('[name="add"]')?.disabled;
        this.unsubscribers = [];

        this.root.addEventListener(
          'change',
          (event) => {
            if (event.target.closest('variant-selects')) {
              this.root.setAttribute('data-lp-selection-pending', '');
              this.updateCheckout();
              return;
            }

            if (event.target.matches('input.product-variant-id')) {
              queueMicrotask(() => {
                if (!this.isConnected) return;
                this.root.removeAttribute('data-lp-selection-pending');
                if (!event.target.value) this.serverBlocked = true;
                this.syncQuantity(true);
                this.updateDetailsVisibility();
              });
              return;
            }

            if (event.target === this.quantity) {
              this.desiredQuantity = this.quantity.value;
              this.syncQuantity(false);
            }
          },
          { signal }
        );

        this.root.addEventListener(
          'input',
          (event) => {
            if (event.target !== this.quantity) return;
            this.desiredQuantity = this.quantity.value;
            this.updateCheckout();
          },
          { signal }
        );

        this.root.addEventListener(
          'submit',
          (event) => {
            if (event.target.id !== `product-form-${this.sectionId}`) return;
            const pending = this.root.hasAttribute('data-lp-selection-pending');
            const id = this.root.querySelector('input.product-variant-id')?.value;
            const invalidQuantity = this.quantity && !this.quantity.checkValidity();
            if (!pending && id && !this.serverBlocked && !this.quantityExhausted && !invalidQuantity) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            if (invalidQuantity && !this.quantityExhausted) {
              this.quantity.reportValidity();
            } else {
              const message = pending
                ? 'Please wait for your selection to finish updating.'
                : 'This selection is not currently available in the requested quantity.';
              this.root.querySelector('product-form')?.handleErrorMessage(message);
            }
          },
          { signal, capture: true }
        );

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers.push(
            subscribe(PUB_SUB_EVENTS.quantityUpdate, () => queueMicrotask(() => this.syncQuantity(true)))
          );
          this.unsubscribers.push(
            subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
              if (String(data?.sectionId || '') !== this.sectionId || !data.html) return;
              const incomingButton = data.html.getElementById(`ProductSubmitButton-${this.sectionId}`);
              this.serverBlocked = !data.variant?.available || !incomingButton || incomingButton.hasAttribute('disabled');
              this.root.removeAttribute('data-lp-selection-pending');
              this.syncThumbnails(data.html);

              if (!data.variant?.featured_media?.id) {
                const active = data.html.querySelector('media-gallery .is-active[data-media-id]');
                if (active) this.root.querySelector('media-gallery')?.setActiveMedia(active.dataset.mediaId, false);
              }

              this.syncQuantity(true);
              this.updateDetailsVisibility();
            })
          );
        }

        this.syncQuantity(true);
        this.updateDetailsVisibility();
      }

      disconnectedCallback() {
        this.unsubscribers?.forEach((unsubscribe) => unsubscribe?.());
        this.controller?.abort();
        this.controller = null;
      }

      syncQuantity(normalize) {
        const input = this.quantity;
        if (input) {
          const ruleMin = Math.max(1, Number(input.dataset.min) || 1);
          const step = Math.max(1, Number(input.step) || 1);
          const inCart = Math.max(0, Number(input.dataset.cartQuantity) || 0);
          const minimum = inCart >= ruleMin ? Math.min(ruleMin, step) : ruleMin;
          const maximum =
            input.hasAttribute('data-max') && input.dataset.max !== ''
              ? Math.max(0, Number(input.dataset.max) - inCart)
              : null;

          input.required = true;
          input.min = String(minimum);
          if (maximum !== null && Number.isFinite(maximum)) input.max = String(maximum);
          else input.removeAttribute('max');

          this.quantityExhausted = maximum !== null && maximum < minimum;
          if (normalize && !this.quantityExhausted) {
            let value = Number(this.desiredQuantity);
            if (!Number.isInteger(value) || value < minimum) value = minimum;
            if (maximum !== null) value = Math.min(value, maximum);
            value = minimum + Math.floor((value - minimum) / step) * step;
            input.value = String(value);
            this.desiredQuantity = input.value;
          }

          const quantityInput = input.closest('quantity-input');
          const minus = quantityInput?.querySelector('[name="minus"]');
          const plus = quantityInput?.querySelector('[name="plus"]');
          const value = Number(input.value);
          this.setButtonDisabled(minus, this.quantityExhausted || value <= minimum);
          this.setButtonDisabled(plus, this.quantityExhausted || (maximum !== null && value + step > maximum));

          const message = this.root.querySelector('[data-lp-quantity-message]');
          if (message) message.hidden = !this.quantityExhausted;
        }
        this.updateCheckout();
      }

      setButtonDisabled(button, disabled) {
        if (!button) return;
        button.disabled = disabled;
        button.classList.toggle('disabled', disabled);
      }

      updateCheckout() {
        const pending = this.root.hasAttribute('data-lp-selection-pending');
        const id = this.root.querySelector('input.product-variant-id')?.value;
        const blocked = pending || !id || this.serverBlocked || !!this.quantityExhausted;
        const button = this.root.querySelector('[name="add"]');
        if (button) button.disabled = blocked;

        const accelerated = this.root.querySelector('.shopify-payment-button');
        if (accelerated) {
          const unavailable = blocked || !!(this.quantity && !this.quantity.checkValidity());
          accelerated.hidden = unavailable;
          accelerated.inert = unavailable;
        }

        const paymentTrigger = this.root.querySelector('[data-lp-payment-open]');
        if (paymentTrigger) {
          paymentTrigger.hidden = pending || !id;
          paymentTrigger.setAttribute('aria-hidden', pending || !id ? 'true' : 'false');
        }
      }

      updateDetailsVisibility() {
        const unavailable = !this.root.querySelector('input.product-variant-id')?.value;
        const description = this.querySelector('.lp-desc');
        const notice = this.querySelector('[data-lp-details-unavailable]');
        if (description) description.hidden = unavailable;
        if (notice) notice.hidden = !unavailable;
      }

      syncThumbnails(html) {
        const gallery = this.root.querySelector('media-gallery');
        const thumbnails = gallery?.querySelector('[id^="GalleryThumbnails-"]');
        const list = thumbnails?.querySelector('[id^="Slider-Thumbnails-"]');
        const source = html.querySelector('media-gallery [id^="Slider-Thumbnails-"]');
        if (!gallery || !list || !source) return;

        const existing = new Map(
          [...list.querySelectorAll('[data-target]')].map((element) => [element.dataset.target, element])
        );
        const fragment = document.createDocumentFragment();

        [...source.querySelectorAll('[data-target]')].forEach((incoming, index) => {
          let node = existing.get(incoming.dataset.target);
          if (!node) {
            node = incoming.cloneNode(true);
            node.querySelector('button')?.addEventListener('click', () => gallery.setActiveMedia(node.dataset.target, false));
          }
          node.dataset.mediaPosition = String(index + 1);
          const label = incoming.querySelector('button')?.getAttribute('aria-label');
          if (label) node.querySelector('button')?.setAttribute('aria-label', label);
          fragment.appendChild(node);
        });

        list.replaceChildren(fragment);
        thumbnails.resetPages?.();
      }
    }
  );
}
